import { Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from './ThemeProvider';

/**
 * Glass redesign — 2-button segmented pill matching the prototype's
 * `.gp-theme-toggle`. Renders sun + moon side-by-side; the active
 * button has the elevated chip background.
 *
 * "System" stays available under the hood (provider tracks it,
 * matchMedia drives detection on first paint), but it's not a
 * button in the UI — clicking sun/moon explicitly sets that mode
 * and persists. This matches the prototype's UX exactly: only two
 * affordances visible, OS detection happens silently.
 *
 * Reads `resolvedTheme` (not `theme`) for the active state so the
 * pill always shows what's actually painted right now — even when
 * the user is in 'system' mode and the OS preference is dark.
 */
export const ThemeToggle = () => {
  const { resolvedTheme, setTheme } = useTheme();

  return (
    <span
      role="group"
      aria-label="Theme"
      className={cn(
        'inline-flex items-center gap-0 rounded-full border p-0.5',
        'border-border bg-secondary',
      )}
    >
      <button
        type="button"
        onClick={() => setTheme('light')}
        aria-label="Light"
        aria-pressed={resolvedTheme === 'light'}
        className={cn(
          'inline-flex h-6 w-[26px] items-center justify-center rounded-full transition-colors duration-100',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-accent)]',
          resolvedTheme === 'light'
            ? 'bg-background text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.06)]'
            : 'text-foreground-muted hover:text-foreground',
        )}
      >
        <Sun className="size-3.5" />
      </button>
      <button
        type="button"
        onClick={() => setTheme('dark')}
        aria-label="Dark"
        aria-pressed={resolvedTheme === 'dark'}
        className={cn(
          'inline-flex h-6 w-[26px] items-center justify-center rounded-full transition-colors duration-100',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-accent)]',
          resolvedTheme === 'dark'
            ? 'bg-background text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.06)]'
            : 'text-foreground-muted hover:text-foreground',
        )}
      >
        <Moon className="size-3.5" />
      </button>
    </span>
  );
};
